import { DatePipe } from '@angular/common';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { Button, Modal, ThumbnailItem, ToggleButton } from '@ntv360/component-pantry';
import {
    MEDIA_DETAILS_LEFT,
    MEDIA_DETAILS_RIGHT,
    MODAL_NAV_ICONS,
} from '../../../../features/media-library/constants';

@Component({
    selector: 'app-media-details-modal',
    standalone: true,
    imports: [Modal, Button, ToggleButton],
    templateUrl: './media-details-modal.component.html',
    styleUrl: './media-details-modal.component.scss',
    providers: [DatePipe],
})
export class MediaDetailsModalComponent {
    /** Whether the modal is visible */
    public isVisible = input.required<boolean>();

    /** The currently selected media item to display */
    public item = input.required<ThumbnailItem | null>();

    /** The list of all content items for navigation */
    public contents = input<ThumbnailItem[]>([]);

    /** Emits when the modal is closed */
    public modalClose = output<void>();

    /** Emits the item to be deleted */
    public deleteItem = output<ThumbnailItem>();

    /** Emits when the user navigates to a different item (prev/next) */
    public navigate = output<ThumbnailItem>();

    /** Navigation icons configuration for the modal toolbar */
    protected modalNavIcons = MODAL_NAV_ICONS;

    /** Left section detail fields (e.g. Name, Owner, Role) */
    protected mediaDetailsLeft = MEDIA_DETAILS_LEFT;

    /** Right section technical detail fields (e.g. Duration, File Size) */
    protected mediaDetailsRight = MEDIA_DETAILS_RIGHT;

    /** Date pipe for formatting dates */
    private datePipe = inject(DatePipe);

    /** The currently selected media item for the modal */
    public techmagicHide = input<boolean>(true);

    /** Whether the content is marked as a filler */
    protected isContentFiller = signal<boolean>(false);

    /** Whether the delete confirmation modal is visible */
    protected showDeleteConfirmation = signal<boolean>(false);

    /** The index of the current item within the contents array */
    protected currentIndex = computed(() => {
        const item = this.item();
        return item ? this.contents().findIndex((i) => i.id === item.id) : -1;
    });

    /** The total number of items in the contents list */
    protected totalItems = computed(() => this.contents().length);

    /**
     * Handles modal navigation toolbar actions
     * @param action - The action to perform (view, delete, prev, next)
     */
    protected handleNavAction(action?: string): void {
        switch (action) {
            case 'view':
                this.viewContentInNewTab();
                break;
            case 'delete':
                this.showDeleteConfirmation.set(true);
                break;
            case 'prev':
                this.prevItem();
                break;
            case 'next':
                this.nextItem();
                break;
        }
    }

    /**
     * Checks if a navigation icon should be disabled
     * @param check - The condition to check ('isFirst' or 'isLast')
     * @returns Whether the navigation action is disabled
     */
    protected isNavDisabled(check?: string): boolean {
        if (check === 'isFirst') return this.currentIndex() <= 0;
        if (check === 'isLast') return this.currentIndex() >= this.totalItems() - 1;
        return false;
    }

    /**
     * Opens the current item's source URL in a new browser tab
     */
    private viewContentInNewTab(): void {
        const item = this.item();
        if (item?.src) {
            window.open(item.src, '_blank');
        }
    }

    /**
     * Navigates to the next item in the contents list
     */
    private nextItem(): void {
        const idx = this.currentIndex();
        if (idx < this.totalItems() - 1) {
            this.navigate.emit(this.contents()[idx + 1]);
        }
    }

    /**
     * Navigates to the previous item in the contents list
     */
    private prevItem(): void {
        const idx = this.currentIndex();
        if (idx > 0) {
            this.navigate.emit(this.contents()[idx - 1]);
        }
    }

    /**
     * Toggles the filler status of the content
     * @param checked - Whether the content should be marked as filler
     */
    protected setContentToFilter(checked: boolean): void {
        this.isContentFiller.set(checked);
    }

    /**
     * Resolves a detail value from the item by key, with optional pipe and fallback
     * @param item - The media item object
     * @param key - The property key to resolve
     * @param pipe - Optional pipe to apply (e.g. 'date')
     * @param fallback - Fallback value if the key is not found
     * @param format - Optional format for the pipe (e.g. 'MMMM d, y')
     * @returns The resolved string value
     */
    protected resolveDetailValue(
        item: any,
        key: string,
        pipe?: string,
        fallback?: string,
        format?: string,
    ): string {
        const val = item ? item[key] : null;

        if (val === null || val === undefined || val === '') return fallback || '--';

        if (pipe === 'date') {
            return this.datePipe.transform(val, format || 'MMMM d, y') || fallback || '--';
        }

        return val.toString() || fallback || '--';
    }

    /**
     * Confirms deletion of the current item and closes the confirmation modal
     */
    protected confirmDelete(): void {
        const item = this.item();
        if (item) {
            this.deleteItem.emit(item);
        }
        this.showDeleteConfirmation.set(false);
    }

    /**
     * Cancels the delete action and closes the confirmation modal
     */
    protected cancelDelete(): void {
        this.showDeleteConfirmation.set(false);
    }

    /**
     * Closes the modal
     */
    protected onClose(): void {
        this.showDeleteConfirmation.set(false);
        this.modalClose.emit();
    }
}
